/**
 * AI Recruit - Duplicate Detection Modal
 * Modal component for showing duplicate detection results and user confirmation
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { 
  ExclamationTriangleIcon,
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentTextIcon,
  CalendarIcon,
  CheckIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { resumesApi } from '../../services/api'
import { toast } from 'react-hot-toast'
import { usePermission } from '@/hooks/usePermission'
import Tooltip from '@/components/common/Tooltip'

export interface DuplicateCandidate {
  resume_id: string
  candidate_name: string | null
  candidate_email: string | null
  candidate_phone: string | null
  original_filename: string
  created_at: string
  status: string
  match_score: number
  match_type: string
  confidence: number
  blacklist: boolean
}

export interface ExtractedInfo {
  candidate_name: string | null
  candidate_email: string | null
  candidate_phone: string | null
  file_name: string
  file_size: number
}

export interface DuplicateDetectionData {
  has_duplicates: boolean
  extracted_info: ExtractedInfo
  duplicate_candidates: DuplicateCandidate[]
  total_duplicates_found: number
  popup_config: {
    title: string
    message: string
    show_upload_button: boolean
    upload_endpoint: string
    cancel_text: string
    upload_text: string
  }
  current_resume_id?: string // Add current resume ID for blacklisting
}

interface DuplicateDetectionModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadAnyway: () => void
  duplicateData: DuplicateDetectionData | null
  isUploading?: boolean
  onBlacklistChange?: (blacklistedIds: string[]) => void
}

const DuplicateDetectionModal: React.FC<DuplicateDetectionModalProps> = ({
  isOpen,
  onClose,
  onUploadAnyway,
  duplicateData,
  isUploading = false,
  onBlacklistChange
}) => {
  const { can } = usePermission()
  // Hidden (not disabled) without resumes.blacklist — bug fix, 2026-08-26,
  // matching this app's hide-not-disable convention everywhere else. Also
  // closes a real bug this was the root cause of: with the checkbox gone,
  // blacklistedIds can never be populated by a user lacking the
  // permission, so UploadModal.tsx's automatic post-upload blacklist call
  // (fired whenever blacklistedIds.length > 0) can no longer fire for them
  // either — no more surprise "Missing permission: resumes.blacklist"
  // toast after an otherwise-successful upload.
  const canBlacklist = can('resumes.blacklist')
  const [blacklistedIds, setBlacklistedIds] = useState<string[]>([])
  const [isBlacklisting, setIsBlacklisting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showResumePopup, setShowResumePopup] = useState(false)
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null)
  const [resumeDetails, setResumeDetails] = useState<any>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  if (!isOpen || !duplicateData) return null

  // Function to refresh resume list
  const refreshResumeList = () => {
    console.log('🔄 Refreshing resume list after duplicate modal actions')
    // Invalidate all resume-related queries
    queryClient.invalidateQueries({ queryKey: ['resumes'] })
    queryClient.invalidateQueries({ queryKey: ['resumes-for-matching'] })
    queryClient.invalidateQueries({ queryKey: ['matching-results'] })
    queryClient.invalidateQueries({ queryKey: ['jobs-for-matching'] })
  }

  const handleBlacklistToggle = (resumeId: string) => {
    // Find the candidate to check if it's already blacklisted
    const candidate = duplicateData?.duplicate_candidates.find(c => c.resume_id === resumeId)
    if (candidate?.blacklist) {
      // Don't allow toggling already blacklisted resumes
      return
    }
    
    const newBlacklistedIds = blacklistedIds.includes(resumeId) 
      ? blacklistedIds.filter(id => id !== resumeId)
      : [...blacklistedIds, resumeId]
    
    setBlacklistedIds(newBlacklistedIds)
    
    // Notify parent component of blacklist changes
    if (onBlacklistChange) {
      onBlacklistChange(newBlacklistedIds)
    }
  }

  const handleBlacklistDuplicates = async () => {
    if (blacklistedIds.length === 0) return

    console.log('🔍 Mark for blacklisting clicked with IDs:', blacklistedIds)
    setIsBlacklisting(true)
    try {
      // Call the blacklist API immediately
      const response = await resumesApi.blacklistDuplicates({ 
        blacklisted_resume_ids: blacklistedIds 
      })
      
      console.log('✅ Blacklist API response:', response)
      toast.success(`Successfully blacklisted ${blacklistedIds.length} duplicate resume(s)`)
      
      // Clear the blacklisted IDs since they're now blacklisted
      setBlacklistedIds([])
      
      // Refresh the resume list
      refreshResumeList()
      
      // Close the popup after successful blacklisting
      console.log('🚪 Closing popup after successful blacklisting')
      onClose()
      
    } catch (error: any) {
      console.error('❌ Failed to blacklist duplicates:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to blacklist duplicates')
    } finally {
      setIsBlacklisting(false)
    }
  }

  const handleDeleteResume = async (resumeId: string) => {
    console.log('🗑️ Delete individual resume clicked:', resumeId)
    setIsDeleting(true)
    try {
      await resumesApi.delete(resumeId)
      
      console.log('✅ Successfully deleted resume:', resumeId)
      toast.success('Resume deleted successfully')
      
      // Refresh the resume list
      refreshResumeList()
      
      // Close modal after successful deletion
      onClose()
      
    } catch (error: any) {
      console.error('❌ Failed to delete resume:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to delete resume')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleViewResume = async (resumeId: string) => {
    setSelectedResumeId(resumeId)
    setShowResumePopup(true)
    setIsLoadingDetails(true)
    
    try {
      const response = await resumesApi.getById(resumeId)
      console.log('Resume details response:', response.data)
      setResumeDetails(response.data)
    } catch (error: any) {
      console.error('Failed to fetch resume details:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to load resume details')
    } finally {
      setIsLoadingDetails(false)
    }
  }

  const handleCloseResumePopup = () => {
    setShowResumePopup(false)
    setSelectedResumeId(null)
    setResumeDetails(null)
  }

  // Enhanced close handler that refreshes the resume list
  const handleClose = () => {
    console.log('🚪 Duplicate modal closing, refreshing resume list')
    refreshResumeList()
    onClose()
  }

  const truncateFileName = (name: string, maxLength: number = 25): string => {
    if (name.length <= maxLength) return name
    return `${name.slice(0, maxLength)}...`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'vector':
        return 'bg-blue-100 text-blue-800'
      case 'name':
        return 'bg-green-100 text-green-800'
      case 'email':
        return 'bg-purple-100 text-purple-800'
      case 'phone':
        return 'bg-orange-100 text-orange-800'
      case 'multiple':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getMatchTypeLabel = (matchType: string) => {
    switch (matchType) {
      case 'vector':
        return 'AI Similarity'
      case 'name':
        return 'Name Match'
      case 'email':
        return 'Email Match'
      case 'phone':
        return 'Phone Match'
      case 'multiple':
        return 'Multiple Matches'
      default:
        return 'Match'
    }
  }

  const renderMatchBadges = (matchType: string) => {
    if (matchType === 'email_phone') {
      return (
        <>
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
            Email Match
          </span>
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
            Phone Match
          </span>
        </>
      )
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMatchTypeColor(matchType)}`}>
        {getMatchTypeLabel(matchType)}
      </span>
    )
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-4 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:align-middle sm:max-w-3xl sm:w-full sm:p-5 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {duplicateData.popup_config.title}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {duplicateData.popup_config.message}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <>
            <div className="space-y-4">
              {/* Extracted Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-base font-medium text-blue-900 mb-2 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  Extracted Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Name:</span>
                      <span className="text-sm text-blue-700">
                        {duplicateData.extracted_info.candidate_name || 'Not found'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <EnvelopeIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Email:</span>
                      <span className="text-sm text-blue-700">
                        {duplicateData.extracted_info.candidate_email || 'Not found'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Phone:</span>
                      <span className="text-sm text-blue-700">
                        {duplicateData.extracted_info.candidate_phone || 'Not found'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">File:</span>
                      {duplicateData.extracted_info.file_name.length > 25 ? (
                        <Tooltip content={duplicateData.extracted_info.file_name}>
                          <span className="text-blue-700 ml-2 cursor-default">
                            {truncateFileName(duplicateData.extracted_info.file_name)}
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="text-blue-700 ml-2">{duplicateData.extracted_info.file_name}</span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">Size:</span>
                      <span className="text-blue-700 ml-2">{formatFileSize(duplicateData.extracted_info.file_size)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Duplicate Candidates */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">
                  Potential Duplicates ({duplicateData.total_duplicates_found})
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {duplicateData.duplicate_candidates.map((candidate, index) => (
                    <div key={candidate.resume_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {/* Blacklist Checkbox */}
                          {canBlacklist && (
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`blacklist-${candidate.resume_id}`}
                                checked={blacklistedIds.includes(candidate.resume_id)}
                                onChange={() => handleBlacklistToggle(candidate.resume_id)}
                                disabled={candidate.blacklist}
                                className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                                  candidate.blacklist ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              />
                            </div>
                          )}
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h5 className="font-medium text-gray-900">
                                {candidate.candidate_name || 'Unknown Name'}
                              </h5>
                              {renderMatchBadges(candidate.match_type)}
                              <span className="text-sm font-medium text-gray-500">
                                {(candidate.match_score * 100).toFixed(1)}% match
                              </span>
                              {candidate.blacklist && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                                  🚫 Blacklisted
                                </span>
                              )}
                            </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                              <span>{candidate.candidate_email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <PhoneIcon className="h-4 w-4 text-gray-400" />
                              <span>{candidate.candidate_phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                              <span className="truncate">{candidate.original_filename}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                              <span>{formatDate(candidate.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        {/* View Button */}
                        <button
                          onClick={() => handleViewResume(candidate.resume_id)}
                          className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View resume details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteResume(candidate.resume_id)}
                          disabled={isDeleting}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete this resume"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 flex justify-between items-center pt-4 border-t border-gray-200">
            {/* Blacklist Button */}
            {canBlacklist && blacklistedIds.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBlacklistDuplicates}
                  disabled={isBlacklisting || isUploading}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isBlacklisting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      <span>Blacklisting...</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      <span>Blacklist {blacklistedIds.length} Selected Resume(s)</span>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {duplicateData.popup_config.cancel_text}
              </button>
              <button
                onClick={onUploadAnyway}
                disabled={isUploading}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <span>{duplicateData.popup_config.upload_text}</span>
                )}
              </button>
            </div>
          </div>
          </>
        </div>
      </div>

      {/* Resume Details Popup */}
      {showResumePopup && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseResumePopup}
            />
            
            {/* Popup panel */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Resume Details
                </h3>
                <button
                  onClick={handleCloseResumePopup}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-[70vh] overflow-y-auto">
                {isLoadingDetails ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading resume details...</span>
                  </div>
                ) : resumeDetails ? (
                  <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Name:</span>
                          <span className="ml-2">{resumeDetails.candidate_name || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Email:</span>
                          <span className="ml-2">{resumeDetails.analysis?.personal_info?.email || resumeDetails.candidate_email || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Phone:</span>
                          <span className="ml-2">{resumeDetails.analysis?.personal_info?.phone || resumeDetails.candidate_phone || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Status:</span>
                          <span className="ml-2">{resumeDetails.status || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Professional Title */}
                    {resumeDetails.analysis?.personal_info?.professional_title && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Professional Title</h4>
                        <p className="text-blue-800">{resumeDetails.analysis.personal_info.professional_title}</p>
                      </div>
                    )}

                    {/* Skills */}
                    {(resumeDetails.analysis?.skills_analysis?.technical_skills || resumeDetails.analysis?.skills_analysis?.programming_languages) && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Skills</h4>
                        <div className="space-y-3">
                          {/* Technical Skills */}
                          {resumeDetails.analysis?.skills_analysis?.technical_skills && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Technical Skills</h5>
                              <div className="flex flex-wrap gap-2">
                                {resumeDetails.analysis.skills_analysis.technical_skills.map((skill: any, index: number) => (
                                  <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    {typeof skill === 'string' ? skill : skill.skill || skill.name || 'Unknown Skill'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Programming Languages */}
                          {resumeDetails.analysis?.skills_analysis?.programming_languages && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Programming Languages</h5>
                              <div className="flex flex-wrap gap-2">
                                {resumeDetails.analysis.skills_analysis.programming_languages.map((lang: any, index: number) => (
                                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    {typeof lang === 'string' ? lang : lang.skill || lang.name || 'Unknown Language'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Frameworks & Libraries */}
                          {resumeDetails.analysis?.skills_analysis?.frameworks_libraries && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Frameworks & Libraries</h5>
                              <div className="flex flex-wrap gap-2">
                                {resumeDetails.analysis.skills_analysis.frameworks_libraries.map((framework: any, index: number) => (
                                  <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                    {typeof framework === 'string' ? framework : framework.skill || framework.name || 'Unknown Framework'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Experience */}
                    {resumeDetails.analysis?.career_analysis?.work_experience && (
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Work Experience</h4>
                        <div className="space-y-2">
                          {resumeDetails.analysis.career_analysis.work_experience.map((exp: any, index: number) => (
                            <div key={index} className="border-l-2 border-purple-200 pl-3">
                              <div className="font-medium text-gray-900">{exp.position}</div>
                              <div className="text-sm text-gray-600">{exp.company}</div>
                              <div className="text-xs text-gray-500">{exp.start_date} - {exp.end_date}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Failed to load resume details
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { DuplicateDetectionModal }
export default DuplicateDetectionModal
